import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import { API } from '../../App';
import { buildWhatsAppWebUrl } from '../../lib/whatsapp';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const detail = (e, fallback) => typeof e.response?.data?.detail === 'string' ? e.response.data.detail : fallback;

export const AssignmentAlertPrompt = ({ alert, apiReady, onComplete, onRefresh }) => {
  const [phone, setPhone] = useState(alert.phone || '');
  const [prepared, setPrepared] = useState(alert.status === 'MANUAL_READY' ? alert : null);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(alert.lastError || '');
  const running = useRef(false);
  const endpoint = `${API}/assignment-alerts/${alert.id}`;
  const uncertain = ['SENDING', 'UNKNOWN', 'PREPARING'].includes(alert.status);

  const acknowledge = async () => {
    await axios.post(`${endpoint}/handoff`, {}, { skipGlobalErrorToast: true });
    toast.success('WhatsApp opened — tap Send to notify the assigned person.');
    onComplete();
  };
  const openMessage = async (data, reserved) => {
    const win = reserved && !reserved.closed ? reserved : window.open('', '_blank');
    if (!win) { setError('WhatsApp was blocked. Click Open WhatsApp to continue.'); return; }
    win.opener = null;
    win.location.href = buildWhatsAppWebUrl(data.phone, data.message);
    setOpened(true);
    try { win.focus(); } catch { /* browser focus restriction */ }
    await acknowledge();
  };
  const send = async () => {
    if (running.current) return;
    running.current = true; setBusy(true); setError('');
    const reserved = !apiReady && !opened ? window.open('', '_blank') : null;
    if (reserved) reserved.opener = null;
    try {
      if (opened) { await acknowledge(); return; }
      // Always revalidate the current assignment before opening a stored manual message.
      const { data } = await axios.post(`${endpoint}/send`, { phone }, { skipGlobalErrorToast: true });
      if (['API_ACCEPTED', 'WHATSAPP_OPENED', 'SUPERSEDED'].includes(data.status)) {
        reserved?.close();
        toast.success(data.status === 'API_ACCEPTED' ? 'Assignment alert accepted by WhatsApp.' : 'Assignment alert is already resolved.');
        onComplete();
      } else { setPrepared(data); await openMessage(data, reserved); }
    } catch (e) {
      try { if (reserved?.location.href === 'about:blank') reserved.close(); } catch { /* already navigated */ }
      setError(detail(e, 'Could not complete the alert. The assignment is saved; please retry.'));
    } finally { setBusy(false); running.current = false; }
  };

  return <Dialog open onOpenChange={() => {}}>
    <DialogContent showCloseButton={false} data-testid="assignment-alert-dialog"
      className="bg-white grid-cols-1 w-[calc(100%_-_2rem)] max-w-lg max-h-[90vh] overflow-y-auto"
      onEscapeKeyDown={e => e.preventDefault()} onInteractOutside={e => e.preventDefault()}>
      <DialogHeader className="min-w-0 text-left">
        <DialogTitle data-testid="assignment-alert-title" className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-green-600 shrink-0" />
          {alert.kind === 'MANAGER_INQUIRY' ? 'Notify assigned manager' : 'Send fabricator drawings'}
        </DialogTitle>
        <DialogDescription data-testid="assignment-alert-recipient" className="[overflow-wrap:anywhere]">{alert.recipientName} · {alert.subjectCode}</DialogDescription>
      </DialogHeader>
      <div className="min-w-0 space-y-4">
        <p data-testid="assignment-alert-mode" className="text-sm text-slate-600">{apiReady && !prepared ? 'WhatsApp API alert' : 'Prefilled WhatsApp message — tap Send in WhatsApp.'}</p>
        <div><label htmlFor="assignment-alert-phone" className="text-xs font-medium text-slate-600">Recipient WhatsApp number</label>
          <Input id="assignment-alert-phone" data-testid="assignment-alert-phone" type="tel" maxLength={40}
            value={phone} onChange={e => setPhone(e.target.value)} disabled={busy || !!prepared || uncertain} placeholder="Mobile number" /></div>
        <p data-testid="assignment-alert-message" className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm bg-slate-50 p-3 rounded-md max-h-64 overflow-y-auto">{prepared?.message || alert.message}</p>
        {(error || alert.lastError) && <p data-testid="assignment-alert-error" role="alert" className="text-sm text-red-700 break-words">{error || alert.lastError}</p>}
        {uncertain ? <Button data-testid="assignment-alert-refresh" variant="outline" onClick={onRefresh} className="w-full"><RefreshCw className="w-4 h-4 mr-2" />Refresh status</Button> :
          <Button data-testid="assignment-alert-send" onClick={send} disabled={busy || !phone.trim()} className="w-full bg-green-600 hover:bg-green-700">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{opened ? 'Record WhatsApp handoff' : apiReady && !prepared ? 'Send WhatsApp alert' : 'Open WhatsApp'}
          </Button>}
      </div>
    </DialogContent>
  </Dialog>;
};